import React from 'react';
import { facilityTypeColor, facilityTypeLabel } from '../../utils/facility';

/**
 * Floating panel describing the facility region under the cursor: its type,
 * confidence, equipment count, source layer and real-world size. Positioned in
 * screen space next to the pointer, flipping near the viewport edges.
 */
interface Props {
  x: number;
  y: number;
  containerWidth: number;
  containerHeight: number;
  name: string;
  type: string;
  confidence: number;
  equipmentCount: number;
  layer: string;
  width: number; // canvas units
  height: number; // canvas units
  scaleFactor: number; // raw DXF units per canvas unit
}

const TOOLTIP_W = 250;
const OFFSET = 16;

function formatRealSize(units: number): string {
  if (!Number.isFinite(units)) return '—';
  if (units >= 1000) return `${(units / 1000).toFixed(2)} m`;
  return `${Math.round(units)} mm`;
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-2 leading-tight">
    <span className="text-gray-400 shrink-0 w-16">{label}</span>
    <span className="text-gray-800 flex-1 min-w-0 break-words">{children}</span>
  </div>
);

const FacilityZoneTooltip: React.FC<Props> = ({
  x,
  y,
  containerWidth,
  containerHeight,
  name,
  type,
  confidence,
  equipmentCount,
  layer,
  width,
  height,
  scaleFactor,
}) => {
  const left =
    x + OFFSET + TOOLTIP_W > containerWidth ? Math.max(8, x - OFFSET - TOOLTIP_W) : x + OFFSET;
  const top = Math.min(Math.max(8, y + OFFSET), Math.max(8, containerHeight - 170));
  const color = facilityTypeColor(type);

  return (
    <div
      className="pointer-events-none absolute z-20 rounded-lg border border-gray-200 bg-white/95 shadow-xl backdrop-blur-sm px-3 py-2 text-[11px] space-y-1"
      style={{ left, top, width: TOOLTIP_W }}
    >
      <div className="flex items-center gap-2 pb-1 mb-1 border-b border-gray-100">
        <span
          className="inline-block w-3 h-3 rounded-sm border border-black/20 shrink-0"
          style={{ background: color }}
        />
        <span className="font-semibold text-gray-900">{facilityTypeLabel(type)}</span>
        <span className="ml-auto text-[10px] text-gray-400">{equipmentCount} equip.</span>
      </div>

      {name && (
        <Row label="Name">
          <span className="font-medium">{name}</span>
        </Row>
      )}
      <Row label="Confidence">
        <span
          className={
            confidence >= 0.7
              ? 'text-emerald-600'
              : confidence >= 0.5
                ? 'text-amber-600'
                : 'text-gray-500'
          }
        >
          {Math.round(confidence * 100)}%
        </span>
      </Row>
      <Row label="Size">
        {formatRealSize(width * scaleFactor)} × {formatRealSize(height * scaleFactor)}
      </Row>
      {layer && (
        <Row label="Layer">
          <span className="font-mono break-all">{layer}</span>
        </Row>
      )}
    </div>
  );
};

export default FacilityZoneTooltip;
